import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** Chinese mirror of the Thai police clearance certificate guide. */
const TITLE = "泰国无犯罪记录证明（Police Clearance）出境使用完整指南";
const DESC =
  "如何申请泰国无犯罪记录证明、需要准备哪些文件、后续的领事与使领馆认证步骤，以及最常见的退件原因。";
const PATH = "/blog/police-clearance-certificate-guide";
const URL = `/zh${PATH}`;
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "泰国无犯罪记录证明由哪个机关签发？",
    answer:
      "由泰国皇家警察总署（Royal Thai Police）刑事记录部门签发。申请人可本人到场，或依规定委托代理人办理。",
  },
  {
    question: "需要准备哪些文件？",
    answer:
      "通常包括护照或身份证、户籍资料、指纹表、申请表，以及说明用途的文件（例如签证要求信、雇主信或学校录取通知）。用途不同，所需附件也不同。",
  },
  {
    question: "办理需要多久？",
    answer:
      "一般为数个工作日至数周，取决于查核范围与是否需要补件。若还需领事司与使领馆认证，请另行预留时间。",
  },
  {
    question: "证明书有有效期吗？",
    answer:
      "签发机关通常不标注有效期，但受理机关多要求在 3 至 6 个月内签发。请以受理机关的书面要求为准。",
  },
  {
    question: "还需要翻译与认证吗？",
    answer:
      "若在国外使用，通常需要译成目的地语言，并依序办理泰国外交部领事司认证与该国驻泰使领馆认证。海牙成员国的路径可能不同，请先确认。",
  },
  {
    question: "不在泰国境内可以申请吗？",
    answer:
      "可以，通常通过泰国驻当地使领馆提交，或以经认证的委托书委托他人在泰国代办。指纹采集方式请先向受理窗口确认。",
  },
  {
    question: "最常见的退件原因是什么？",
    answer:
      "姓名拼写与护照不一致、指纹表不清晰、未说明用途、证明书签发时间过久，以及认证顺序颠倒。",
  },
];

export const Route = createFileRoute("/zh/blog/police-clearance-certificate-guide")({
  component: ZhPccGuide,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { property: "og:locale", content: "zh" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("police-clearance-certificate-guide") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("police-clearance-certificate-guide", "zh") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("police-clearance-certificate-guide") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "泰国无犯罪记录证明, police clearance 泰国, 泰国良民证, 无犯罪证明认证, 泰国警察总署证明",
        },
      ],
      links: canonicalAndHreflang(PATH, null, "zh"),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "首页", url: "/zh" },
              { name: "指南", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("police-clearance-certificate-guide"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Police Clearance",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const DOCS: string[] = [
  "护照正本（境外使用时以护照拼写为准）",
  "泰国身份证或居留证明（如适用）",
  "户籍资料或住址证明",
  "指纹表（依窗口指定格式采集）",
  "说明用途的文件：签证要求信、雇主信或录取通知",
  "如委托代办：经认证的委托书与代理人身份证件",
];

const STEPS: string[] = [
  "先向受理机关确认所需版本、语言与签发时间要求",
  "备齐身份文件、住址资料与用途说明文件",
  "采集指纹并递交申请（本人到场或依规定委托）",
  "领取证明书并核对姓名拼写、出生日期与签发日期",
  "按目的地要求翻译，并办理领事司认证",
  "办理目的地国驻泰使领馆认证后再递交",
];

const MISTAKES: string[] = [
  "姓名拼写与护照不一致，导致文件无法对应同一人",
  "先翻译认证再确认要求，顺序错误须重办",
  "指纹表不清晰或格式不符",
  "证明书签发时间过久，超出受理机关的时效要求",
  "使用普通复印件而非正本或认证副本",
];

function ZhPccGuide() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/zh" className="text-muted-foreground hover:text-foreground">
          首页
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          指南
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="police-clearance-certificate-guide" locale="zh" />
      <p className="answer-summary speakable-summary">
        泰国无犯罪记录证明由皇家警察总署签发，申请时需备身份文件、住址资料、指纹表与用途说明。
        若在国外使用，通常还需翻译、泰国外交部领事司认证以及目的地国驻泰使领馆认证。先确认受理机关的时效与语言要求，再按顺序办理，可避免重办。
      </p>

      <h2>1. 需要准备的文件</h2>
      <ul>
        {DOCS.map((d) => (
          <li key={d}>{d}</li>
        ))}
      </ul>

      <h2>2. 办理顺序</h2>
      <ol>
        {STEPS.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>

      <h2>3. 常见退件原因</h2>
      <ul>
        {MISTAKES.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>

      <h2>4. 常见问题</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>5. 参考来源与相关服务</h2>
      <p className="text-sm">
        参考：{" "}
        <a href="https://www.royalthaipolice.go.th/" target="_blank" rel="noreferrer">
          泰国皇家警察总署
        </a>{" "}
        ·{" "}
        <a href="https://consular.mfa.go.th/en/publicservice/legalization" target="_blank" rel="noreferrer">
          泰国外交部领事司
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/zh/pcc" className="text-primary underline">
            无犯罪记录证明办理服务
          </Link>
        </li>
        <li>
          <Link to="/zh/lgz" className="text-primary underline">
            文件认证与领事认证服务
          </Link>
        </li>
        <li>
          <Link to="/zh/sworn" className="text-primary underline">
            宣誓／登记译员服务
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        内容复核日期 {PUBLISHED}。本指南仅为规划用的一般信息，不代表任何机关的决定。办理前请向受理机关确认最新要求。
      </p>
    </article>
  );
}
